/* eslint-disable sort-imports */
/**
 * The New / Edit labor standard dialog: the upcoming-orders warning, the
 * product's existing standards, the basics, and one editor per operation.
 */
import AddIcon from "@mui/icons-material/Add";
import PreviewIcon from "@mui/icons-material/Preview";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import {
	Alert,
	AlertTitle,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid2,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useCallback } from "react";

import { AffectedMOsResponse, LaborStandard, OperationType } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { WorkPhase } from "../../../../services/work-phases-api";
import { EmptyState, GhostButton, MonoText, planner,PrimaryButton,SectionLabel } from "../../planner-ui";
import { ProductMenuEmpty, ProductMenuSearch, useProductMenuSearch } from "../../planner-ui/product-menu-search";
import { standardWorkstations } from "../../utils/standard-matching";
import { StandardForm } from "../controller/use-standard-form";
import OperationEditor from "./operation-editor";
import { MasterJob } from "./phase-row";
import { fieldSx, iconButtonSx, menuItemSx, monoFieldSx, monoTableCellSx, tableCellSx, tableHeadCellSx } from "./styles";

// ---------------------------------------------------------------------------

/** Upcoming job orders that use the standard being edited. */
const AffectedOrders: React.FC<{ loading: boolean; affected: AffectedMOsResponse | null }> = ({ loading, affected }) => (
	<Alert
		severity="warning"
		icon={<WarningIcon sx={{ color: planner.partial }} />}
		sx={{
			mb: 3,
			borderRadius: `${planner.radiusSm}px`,
			bgcolor: planner.partialSoft,
			border: `1px solid ${planner.partial}33`,
			color: planner.ink,
			fontFamily: planner.fontUi,
			"& .MuiAlert-message": { fontFamily: planner.fontUi },
		}}
	>
		<AlertTitle sx={{ ...planner.type.bodyStrong, color: planner.partial }}>Upcoming job orders</AlertTitle>
		{loading ? (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<CircularProgress size={16} sx={{ color: planner.partial }} />
				<Typography sx={{ ...planner.type.body }}>Checking for affected job orders...</Typography>
			</Box>
		) : (
			affected && (
				<Box>
					<Typography sx={{ ...planner.type.body, mb: 1 }}>
						<strong>{affected.totalAffectedMOs}</strong> upcoming job order(s) use this labor standard.
						{affected.mosWithAssignments > 0 && (
							<>
								{" "}
								Of these, <strong>{affected.mosWithAssignments}</strong> already have worker assignments.
							</>
						)}
					</Typography>
					<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mb: 1 }}>
						Changes will apply to <strong>new allocations only</strong>. Existing assignments will not be updated automatically.
					</Typography>
					{affected.affectedMOs.length > 0 && (
						<Box sx={{ mt: 1, maxHeight: 150, overflow: "auto" }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={tableHeadCellSx}>Date</TableCell>
										<TableCell sx={tableHeadCellSx}>SKU</TableCell>
										<TableCell sx={tableHeadCellSx}>Qty</TableCell>
										<TableCell sx={tableHeadCellSx}>Assignments</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{affected.affectedMOs.slice(0, 10).map((mo) => (
										<TableRow key={mo.id}>
											<TableCell sx={monoTableCellSx}>{new Date(mo.startTime).toLocaleDateString()}</TableCell>
											<TableCell sx={monoTableCellSx}>{mo.sku}</TableCell>
											<TableCell sx={monoTableCellSx}>{mo.quantity}</TableCell>
											<TableCell sx={tableCellSx}>
												{mo.hasAssignments ? (
													<Chip
														label={`${mo.assignmentCount} workers`}
														size="small"
														sx={{
															borderRadius: `${planner.radiusSm}px`,
															bgcolor: planner.surfaceSubtle,
															border: `1px solid ${planner.hairlineStrong}`,
															color: planner.inkSecondary,
															fontFamily: planner.fontMono,
															fontSize: 11,
														}}
													/>
												) : (
													<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>None</Typography>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
							{affected.affectedMOs.length > 10 && (
								<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 1, display: "block" }}>
									...and {affected.affectedMOs.length - 10} more
								</Typography>
							)}
						</Box>
					)}
				</Box>
			)
		)}
	</Alert>
);

/** One of the product's existing standards: click to edit, × to delete. */
const ExistingStandardChip: React.FC<{
	standard: LaborStandard;
	onEdit: (standard: LaborStandard) => void;
	onDelete: (id: number) => void;
}> = ({ standard, onEdit, onDelete }) => {
	const handleEdit = useCallback(() => onEdit(standard), [onEdit, standard]);
	const handleDelete = useCallback(() => onDelete(standard.id), [onDelete, standard.id]);
	return (
		<Chip
			label={`${standard.recipeGroup ?? "recipe"} · ${standardWorkstations(standard).join(", ")}`}
			onClick={handleEdit}
			onDelete={handleDelete}
			variant="outlined"
			sx={{
				borderRadius: `${planner.radiusSm}px`,
				borderColor: planner.hairlineStrong,
				bgcolor: planner.surface,
				color: planner.navy,
				fontFamily: planner.fontUi,
				fontSize: 12,
				fontWeight: 600,
				"&:hover": { borderColor: planner.navy, bgcolor: planner.surface },
				"& .MuiChip-deleteIcon": {
					color: planner.inkMuted,
					"&:hover": { color: planner.unstaffed },
				},
			}}
		/>
	);
};

// ---------------------------------------------------------------------------

export interface StandardFormDialogProps {
	form: StandardForm;
	products: Product[];
	operationTypes: OperationType[];
	workcenters: string[];
	jobRoles: MasterJob[];
	workPhases: WorkPhase[];
	/** The selected product's other standards, offered when creating a new one. */
	existingForProduct: LaborStandard[];
	onPreview: (standard: LaborStandard) => void;
	onDeleteExisting: (id: number) => void;
}

const StandardFormDialog: React.FC<StandardFormDialogProps> = ({
	form,
	products,
	operationTypes,
	workcenters,
	jobRoles,
	workPhases,
	existingForProduct,
	onPreview,
	onDeleteExisting,
}) => {
	const { formData, selectedProduct, editingStandard } = form;
	const productSearch = useProductMenuSearch(products, selectedProduct?.id);

	const handleProduct = useCallback((e: SelectChangeEvent<number | "">) => form.setProductId(e.target.value as number), [form]);
	const handleRecipeGroup = useCallback((e: React.ChangeEvent<HTMLInputElement>) => form.setField("recipeGroup", e.target.value), [form]);
	const handleBatchSize = useCallback((e: React.ChangeEvent<HTMLInputElement>) => form.setField("batchSize", e.target.value), [form]);
	const handleBatchUnit = useCallback((e: React.ChangeEvent<HTMLInputElement>) => form.setField("batchUnit", e.target.value), [form]);
	const handleDescription = useCallback((e: React.ChangeEvent<HTMLInputElement>) => form.setField("description", e.target.value), [form]);
	const handlePreview = useCallback(() => {
		if (editingStandard) {
			onPreview(editingStandard);
		}
	}, [onPreview, editingStandard]);

	return (
		<Dialog
			open={form.showForm}
			onClose={form.close}
			maxWidth="lg"
			fullWidth
			PaperProps={{ sx: { borderRadius: `${planner.radius}px`, boxShadow: planner.shadowDialog } }}
		>
			<DialogTitle sx={{ pb: 1.5, bgcolor: planner.surface }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
					<Box sx={{ minWidth: 0 }}>
						<Typography sx={{ ...planner.type.pageTitle, color: planner.navy }}>{editingStandard ? "Edit" : "New"} labor standard</Typography>
						{selectedProduct && (
							<Typography noWrap sx={{ ...planner.type.body, color: planner.inkSecondary, mt: 0.25 }}>
								<MonoText>{selectedProduct.itemNumber}</MonoText> {selectedProduct.name}
							</Typography>
						)}
					</Box>
					{editingStandard && (
						<Tooltip title="Preview Timeline">
							<IconButton onClick={handlePreview} size="small" sx={iconButtonSx}>
								<PreviewIcon />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</DialogTitle>
			<DialogContent dividers sx={{ bgcolor: planner.canvas, borderColor: planner.hairline }}>
				{/* Affected MOs Warning */}
				{editingStandard && (form.loadingAffectedMOs || (form.affectedMOs && form.affectedMOs.totalAffectedMOs > 0)) && (
					<AffectedOrders loading={form.loadingAffectedMOs} affected={form.affectedMOs} />
				)}

				{/* Show existing standards for this product */}
				{selectedProduct && existingForProduct.length > 0 && !editingStandard && (
					<Box sx={{ mb: 3 }}>
						<SectionLabel sx={{ mb: 1 }}>Existing standards</SectionLabel>
						<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
							{existingForProduct.map((std) => (
								<ExistingStandardChip key={std.id} standard={std} onEdit={form.edit} onDelete={onDeleteExisting} />
							))}
						</Box>
						<Divider sx={{ mt: 2, borderColor: planner.hairline }} />
					</Box>
				)}

				{/* Basic Info */}
				<SectionLabel sx={{ mb: 1 }}>Basics</SectionLabel>
				<Grid2 container spacing={2} sx={{ mb: 3 }}>
					{!editingStandard && (
						<Grid2 size={{ xs: 12 }}>
							<FormControl fullWidth required sx={fieldSx}>
								<InputLabel>Product</InputLabel>
								<Select value={selectedProduct?.id ?? ""} onChange={handleProduct} label="Product" {...productSearch.selectProps}>
									<ProductMenuSearch query={productSearch.query} onQueryChange={productSearch.setQuery} />
									{productSearch.visible.map((product) => (
										<MenuItem key={product.id} value={product.id} sx={menuItemSx}>
											<Box component="span" sx={{ fontFamily: planner.fontMono }}>
												{product.itemNumber}
											</Box>
											&nbsp;— {product.name}
										</MenuItem>
									))}
									{productSearch.isEmpty && <ProductMenuEmpty />}
								</Select>
							</FormControl>
						</Grid2>
					)}
					<Grid2 size={{ xs: 12, sm: 3 }}>
						{/* One standard per product AND recipe group — the SAP group number,
						    "MAIN" / "ALT", or blank when the product has only one recipe. */}
						<TextField
							fullWidth
							label="Recipe group"
							placeholder="e.g. 50000123, MAIN"
							value={formData.recipeGroup}
							onChange={handleRecipeGroup}
							helperText="Blank when the product has one recipe"
							sx={monoFieldSx}
						/>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 3 }}>
						<TextField
							fullWidth
							required
							label="Batch Size"
							type="number"
							value={formData.batchSize}
							onChange={handleBatchSize}
							inputProps={{ min: 1 }}
							sx={monoFieldSx}
						/>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 2 }}>
						<TextField fullWidth label="Unit" placeholder="KG, EA, L" value={formData.batchUnit} onChange={handleBatchUnit} sx={monoFieldSx} />
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 4 }}>
						<TextField fullWidth label="Description" value={formData.description} onChange={handleDescription} sx={fieldSx} />
					</Grid2>
				</Grid2>

				{/* Operations */}
				<Box sx={{ mb: 2 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
						<SectionLabel>Operations</SectionLabel>
						<GhostButton startIcon={<AddIcon />} onClick={form.addOperation} size="small">
							Add Operation
						</GhostButton>
					</Box>

					{formData.operations.length === 0 && (
						<Box
							sx={{
								border: `${planner.borderWidth}px dashed ${planner.hairlineStrong}`,
								borderRadius: `${planner.radius}px`,
								bgcolor: planner.surface,
							}}
						>
							<EmptyState compact title="No operations defined" body='Click "Add Operation" to start.' />
						</Box>
					)}

					{formData.operations.map((operation, opIndex) => (
						<OperationEditor
							// Rows have no id of their own until saved; position is their identity here.
							key={opIndex} // eslint-disable-line react/no-array-index-key
							opIndex={opIndex}
							operation={operation}
							operations={formData.operations}
							operationTypes={operationTypes}
							workcenters={workcenters}
							jobRoles={jobRoles}
							workPhases={workPhases}
							usedParallelGroups={form.usedParallelGroups}
							onChange={form.changeOperation}
							onRemove={form.removeOperation}
							onAddPhase={form.addPhase}
							onRemovePhase={form.removePhase}
							onChangePhase={form.changePhase}
						/>
					))}
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 2, bgcolor: planner.surface }}>
				<GhostButton onClick={form.close}>Cancel</GhostButton>
				<PrimaryButton onClick={form.handleSubmit} disabled={!form.canSubmit}>
					{form.saving ? <CircularProgress size={24} sx={{ color: planner.paper }} /> : editingStandard ? "Update Standard" : "Create Standard"}
				</PrimaryButton>
			</DialogActions>
		</Dialog>
	);
};

export default StandardFormDialog;
